import { Box } from "@mui/material";
import { palette } from "../theme";

export interface PillTabsItem<T extends string> {
  value: T;
  label: string;
  badge?: number;
}

interface PillTabsProps<T extends string> {
  items: PillTabsItem<T>[];
  value: T;
  onChange: (v: T) => void;
}

export function PillTabs<T extends string>({
  items,
  value,
  onChange,
}: PillTabsProps<T>): JSX.Element {
  return (
    <Box
      role="tablist"
      sx={{
        display: "inline-flex",
        gap: "6px",
        padding: "4px",
        borderRadius: "999px",
        backgroundColor: palette.surface,
        border: `1px solid ${palette.border}`,
      }}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <Box
            key={item.value}
            role="tab"
            aria-selected={active}
            tabIndex={0}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onChange(item.value);
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 14px",
              borderRadius: "999px",
              cursor: "pointer",
              userSelect: "none",
              fontFamily: '"Geist", system-ui, sans-serif',
              fontSize: "0.85rem",
              fontWeight: 500,
              letterSpacing: "0.01em",
              color: active ? palette.surface : palette.ink,
              backgroundColor: active ? palette.ink : "transparent",
              transition: "background-color 140ms ease, color 140ms ease",
              "&:hover": {
                backgroundColor: active ? palette.ink : palette.sageBg,
              },
            }}
          >
            {item.label}
            {typeof item.badge === "number" && item.badge > 0 && (
              <Box
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: "0.7rem",
                  padding: "1px 7px",
                  borderRadius: "999px",
                  backgroundColor: active ? "rgba(255,255,255,0.18)" : palette.border,
                  color: active ? palette.surface : palette.ink,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {item.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
